import * as XLSX from 'xlsx-js-style';
import { applyColumnWidths } from './columnWidthCalculator';

interface ISupplementAnalysisProduct {
  id?: number;
  supplement_id?: number;
  batch_id?: number;
  batch_initials?: string;
  supplement_classification_id: number;
  supplement_classification: string;
  trade_name: string;
  total_consumption_period: number;
  analyzed_period: number;
  supplemented_animals: number;
  average_weight_of_supplemented_animals: number;
  total_weight_of_supplemented_animals: number;
  uas_total_number_of_animals_supplemented: number;
  average_consumption_in_the_period_kg: number;
  average_consumption_in_the_period_kg_pct: number;
  average_consumption_in_the_period_ua: number;
  average_consumption_in_the_period_ua_pct: number;
  replacement_frequency: number;
  replacement_frequency_pct: number;
}

export const exportSupplementationAnalysisToExcel = (
  analysisProduct: ISupplementAnalysisProduct[],
  analysisBatch: ISupplementAnalysisProduct[],
  startDate?: Date,
  endDate?: Date
) => {
  const workbook = XLSX.utils.book_new();

  const productDataWithPct = analysisProduct
    .filter(item => !item.batch_initials)
    .map((item) => ({
      'Nome do Produto': item.trade_name,
      'Classificação do Produto': item.supplement_classification,
      'Consumo Total no Período (Kg)': item.total_consumption_period,
      'Período Analisado (dias)': item.analyzed_period,
      'Animais Suplementados': item.supplemented_animals,
      'Peso Médio dos Animais (Kg)': parseFloat(item.average_weight_of_supplemented_animals.toFixed(2)),
      'Peso Total dos Animais (Kg)': parseFloat(item.total_weight_of_supplemented_animals.toFixed(2)),
      'Consumo Médio (g/kgPV.dia)': item.average_consumption_in_the_period_kg,
      'Consumo Médio (g.UA/dia)': item.average_consumption_in_the_period_ua,
      'Frequência de Reposição (dias)': item.replacement_frequency,
      '_kg_pct': item.average_consumption_in_the_period_kg_pct || 0,
      '_ua_pct': item.average_consumption_in_the_period_ua_pct || 0,
      '_freq_pct': item.replacement_frequency_pct || 0
    }));

  const productData = productDataWithPct.map(({ _kg_pct, _ua_pct, _freq_pct, ...rest }) => rest);

  if (productData.length > 0) {
    const productWorksheet = XLSX.utils.json_to_sheet(productData);
    
    const range = XLSX.utils.decode_range(productWorksheet['!ref'] || 'A1');
    
    const getConsumptionColor = (percentage: number): string => {
      if (percentage > 15) {
        return 'FF0000';
      } else if (percentage >= 5 && percentage <= 15) {
        return 'FFFF00';
      } else {
        return '00FF00';
      }
    };

    const getFrequencyColor = (percentage: number): string => {
      if (percentage > 30) {
        return 'FF0000';
      } else if (percentage >= 15 && percentage <= 30) {
        return 'FFFF00';
      } else {
        return '00FF00';
      }
    };

    const consumptionKgColIndex = 7;
    const consumptionUaColIndex = 8;
    const frequencyColIndex = 9;

    for (let row = 1; row <= range.e.r; row++) {
      const dataIndex = row - 1;
      
      const kgCellAddress = XLSX.utils.encode_cell({ r: row, c: consumptionKgColIndex });
      if (!productWorksheet[kgCellAddress]) {
        productWorksheet[kgCellAddress] = { v: '', t: 's' };
      }
      const kgPct = productDataWithPct[dataIndex]?._kg_pct ?? 0;
      const kgColor = getConsumptionColor(kgPct);
      if (!productWorksheet[kgCellAddress].s) {
        productWorksheet[kgCellAddress].s = {};
      }
      productWorksheet[kgCellAddress].s.fill = { fgColor: { rgb: kgColor } };

      const uaCellAddress = XLSX.utils.encode_cell({ r: row, c: consumptionUaColIndex });
      if (!productWorksheet[uaCellAddress]) {
        productWorksheet[uaCellAddress] = { v: '', t: 's' };
      }
      const uaPct = productDataWithPct[dataIndex]?._ua_pct ?? 0;
      const uaColor = getConsumptionColor(uaPct);
      if (!productWorksheet[uaCellAddress].s) {
        productWorksheet[uaCellAddress].s = {};
      }
      productWorksheet[uaCellAddress].s.fill = { fgColor: { rgb: uaColor } };

      const freqCellAddress = XLSX.utils.encode_cell({ r: row, c: frequencyColIndex });
      if (!productWorksheet[freqCellAddress]) {
        productWorksheet[freqCellAddress] = { v: '', t: 's' };
      }
      const freqPct = productDataWithPct[dataIndex]?._freq_pct ?? 0;
      const freqColor = getFrequencyColor(freqPct);
      if (!productWorksheet[freqCellAddress].s) {
        productWorksheet[freqCellAddress].s = {};
      }
      productWorksheet[freqCellAddress].s.fill = { fgColor: { rgb: freqColor } };
    }

    const lastRow = range.e.r + 1;
    let legendStartRow = lastRow + 2;

    const consumptionLegendHeader = XLSX.utils.encode_cell({ r: legendStartRow, c: 0 });
    productWorksheet[consumptionLegendHeader] = { v: 'Legenda Consumo', t: 's' };
    productWorksheet[consumptionLegendHeader].s = {
      font: { bold: true },
      alignment: { horizontal: 'left' }
    };

    const consumptionLegendValues = ['>15%', '>=5% <=15%', '<5%'];
    const consumptionLegendColors = ['FF0000', 'FFFF00', '00FF00'];
    
    consumptionLegendValues.forEach((value, index) => {
      const legendCell = XLSX.utils.encode_cell({ r: legendStartRow, c: index + 1 });
      productWorksheet[legendCell] = { v: value, t: 's' };
    });

    consumptionLegendColors.forEach((color, index) => {
      const exampleCell = XLSX.utils.encode_cell({ r: legendStartRow + 1, c: index + 1 });
      productWorksheet[exampleCell] = { v: '', t: 's' };
      productWorksheet[exampleCell].s = {
        fill: { fgColor: { rgb: color } }
      };
    });

    const consumptionExampleHeader = XLSX.utils.encode_cell({ r: legendStartRow + 1, c: 0 });
    productWorksheet[consumptionExampleHeader] = { v: '', t: 's' };

    legendStartRow = legendStartRow + 3;
    const frequencyLegendHeader = XLSX.utils.encode_cell({ r: legendStartRow, c: 0 });
    productWorksheet[frequencyLegendHeader] = { v: 'Legenda Frequência', t: 's' };
    productWorksheet[frequencyLegendHeader].s = {
      font: { bold: true },
      alignment: { horizontal: 'left' }
    };

    const frequencyLegendValues = ['>30%', '>=15% <=30%', '<15%'];
    const frequencyLegendColors = ['FF0000', 'FFFF00', '00FF00'];
    
    frequencyLegendValues.forEach((value, index) => {
      const legendCell = XLSX.utils.encode_cell({ r: legendStartRow, c: index + 1 });
      productWorksheet[legendCell] = { v: value, t: 's' };
    });

    frequencyLegendColors.forEach((color, index) => {
      const exampleCell = XLSX.utils.encode_cell({ r: legendStartRow + 1, c: index + 1 });
      productWorksheet[exampleCell] = { v: '', t: 's' };
      productWorksheet[exampleCell].s = {
        fill: { fgColor: { rgb: color } }
      };
    });

    const frequencyExampleHeader = XLSX.utils.encode_cell({ r: legendStartRow + 1, c: 0 });
    productWorksheet[frequencyExampleHeader] = { v: '', t: 's' };

    productWorksheet['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: legendStartRow + 1, c: 9 }
    });

    applyColumnWidths(productWorksheet, productData);
    XLSX.utils.book_append_sheet(workbook, productWorksheet, 'Consumo por Produto');
  }

  const batchDataWithPct = [...analysisProduct, ...analysisBatch]
    .filter(item => item.batch_initials)
    .map((item) => ({
      'Lote': item.batch_initials,
      'Nome do Produto': item.trade_name,
      'Classificação do Produto': item.supplement_classification,
      'Consumo Total no Período (Kg)': item.total_consumption_period,
      'Período Analisado (dias)': item.analyzed_period,
      'Animais Suplementados': item.supplemented_animals,
      'Peso Médio dos Animais (Kg)': parseFloat(item.average_weight_of_supplemented_animals.toFixed(2)),
      'Peso Total dos Animais (Kg)': parseFloat(item.total_weight_of_supplemented_animals.toFixed(2)),
      'Consumo Médio (g/kgPV.dia)': item.average_consumption_in_the_period_kg,
      'Consumo Médio (g.UA/dia)': item.average_consumption_in_the_period_ua,
      'Frequência de Reposição (dias)': item.replacement_frequency,
      '_kg_pct': item.average_consumption_in_the_period_kg_pct || 0,
      '_ua_pct': item.average_consumption_in_the_period_ua_pct || 0,
      '_freq_pct': item.replacement_frequency_pct || 0
    }));

  const batchData = batchDataWithPct.map(({ _kg_pct, _ua_pct, _freq_pct, ...rest }) => rest);

  if (batchData.length > 0) {
    const batchWorksheet = XLSX.utils.json_to_sheet(batchData);
    
    const range = XLSX.utils.decode_range(batchWorksheet['!ref'] || 'A1');
    
    const getConsumptionColor = (percentage: number): string => {
      if (percentage > 15) {
        return 'FF0000';
      } else if (percentage >= 5 && percentage <= 15) {
        return 'FFFF00';
      } else {
        return '00FF00';
      }
    };

    const getFrequencyColor = (percentage: number): string => {
      if (percentage > 30) {
        return 'FF0000';
      } else if (percentage >= 15 && percentage <= 30) {
        return 'FFFF00';
      } else {
        return '00FF00';
      }
    };

    const consumptionKgColIndex = 8;
    const consumptionUaColIndex = 9;
    const frequencyColIndex = 10;

    for (let row = 1; row <= range.e.r; row++) {
      const dataIndex = row - 1;
      
      const kgCellAddress = XLSX.utils.encode_cell({ r: row, c: consumptionKgColIndex });
      if (!batchWorksheet[kgCellAddress]) {
        batchWorksheet[kgCellAddress] = { v: '', t: 's' };
      }
      const kgPct = batchDataWithPct[dataIndex]?._kg_pct ?? 0;
      const kgColor = getConsumptionColor(kgPct);
      if (!batchWorksheet[kgCellAddress].s) {
        batchWorksheet[kgCellAddress].s = {};
      }
      batchWorksheet[kgCellAddress].s.fill = { fgColor: { rgb: kgColor } };

      const uaCellAddress = XLSX.utils.encode_cell({ r: row, c: consumptionUaColIndex });
      if (!batchWorksheet[uaCellAddress]) {
        batchWorksheet[uaCellAddress] = { v: '', t: 's' };
      }
      const uaPct = batchDataWithPct[dataIndex]?._ua_pct ?? 0;
      const uaColor = getConsumptionColor(uaPct);
      if (!batchWorksheet[uaCellAddress].s) {
        batchWorksheet[uaCellAddress].s = {};
      }
      batchWorksheet[uaCellAddress].s.fill = { fgColor: { rgb: uaColor } };

      const freqCellAddress = XLSX.utils.encode_cell({ r: row, c: frequencyColIndex });
      if (!batchWorksheet[freqCellAddress]) {
        batchWorksheet[freqCellAddress] = { v: '', t: 's' };
      }
      const freqPct = batchDataWithPct[dataIndex]?._freq_pct ?? 0;
      const freqColor = getFrequencyColor(freqPct);
      if (!batchWorksheet[freqCellAddress].s) {
        batchWorksheet[freqCellAddress].s = {};
      }
      batchWorksheet[freqCellAddress].s.fill = { fgColor: { rgb: freqColor } };
    }

    const lastRow = range.e.r + 1;
    let legendStartRow = lastRow + 2;

    const consumptionLegendHeader = XLSX.utils.encode_cell({ r: legendStartRow, c: 0 });
    batchWorksheet[consumptionLegendHeader] = { v: 'Legenda Consumo', t: 's' };
    batchWorksheet[consumptionLegendHeader].s = {
      font: { bold: true },
      alignment: { horizontal: 'left' }
    };

    const consumptionLegendValues = ['>15%', '>=5% <=15%', '<5%'];
    const consumptionLegendColors = ['FF0000', 'FFFF00', '00FF00'];
    
    consumptionLegendValues.forEach((value, index) => {
      const legendCell = XLSX.utils.encode_cell({ r: legendStartRow, c: index + 1 });
      batchWorksheet[legendCell] = { v: value, t: 's' };
    });

    consumptionLegendColors.forEach((color, index) => {
      const exampleCell = XLSX.utils.encode_cell({ r: legendStartRow + 1, c: index + 1 });
      batchWorksheet[exampleCell] = { v: '', t: 's' };
      batchWorksheet[exampleCell].s = {
        fill: { fgColor: { rgb: color } }
      };
    });

    const consumptionExampleHeader = XLSX.utils.encode_cell({ r: legendStartRow + 1, c: 0 });
    batchWorksheet[consumptionExampleHeader] = { v: '', t: 's' };

    legendStartRow = legendStartRow + 3;
    const frequencyLegendHeader = XLSX.utils.encode_cell({ r: legendStartRow, c: 0 });
    batchWorksheet[frequencyLegendHeader] = { v: 'Legenda Frequência', t: 's' };
    batchWorksheet[frequencyLegendHeader].s = {
      font: { bold: true },
      alignment: { horizontal: 'left' }
    };

    const frequencyLegendValues = ['>30%', '>=15% <=30%', '<15%'];
    const frequencyLegendColors = ['FF0000', 'FFFF00', '00FF00'];
    
    frequencyLegendValues.forEach((value, index) => {
      const legendCell = XLSX.utils.encode_cell({ r: legendStartRow, c: index + 1 });
      batchWorksheet[legendCell] = { v: value, t: 's' };
    });

    frequencyLegendColors.forEach((color, index) => {
      const exampleCell = XLSX.utils.encode_cell({ r: legendStartRow + 1, c: index + 1 });
      batchWorksheet[exampleCell] = { v: '', t: 's' };
      batchWorksheet[exampleCell].s = {
        fill: { fgColor: { rgb: color } }
      };
    });

    const frequencyExampleHeader = XLSX.utils.encode_cell({ r: legendStartRow + 1, c: 0 });
    batchWorksheet[frequencyExampleHeader] = { v: '', t: 's' };

    batchWorksheet['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: legendStartRow + 1, c: 10 }
    });

    applyColumnWidths(batchWorksheet, batchData);
    XLSX.utils.book_append_sheet(workbook, batchWorksheet, 'Consumo por Lote');
  }

  const excelBuffer = XLSX.write(workbook, { 
    bookType: 'xlsx', 
    type: 'array' 
  });

  const blob = new Blob([excelBuffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  
  let filename = 'suplementacao-analise';
  if (startDate && endDate) {
    const startStr = startDate.toISOString().split('T')[0];
    const endStr = endDate.toISOString().split('T')[0];
    filename = `suplementacao-analise-${startStr}-${endStr}.xlsx`;
  } else {
    filename = `suplementacao-analise-${new Date().toISOString().split('T')[0]}.xlsx`;
  }
  
  link.download = filename;
  
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
